import React, { useEffect } from "react";
import { Alert, App } from "antd";
import { useTranslation } from "react-i18next";
import { useCurrentEmployee } from "../contexts/useCurrentEmployee";
import { ACCESS_GATES, type AccessGate } from "@ds/config/accessGates";

/**
 * Runtime read of a single gate — the hook form of `withGate` / `withContentGate`
 * for affordances that stay MOUNTED but must not act (a header button, a section
 * action, a bulk action) rather than pages that must not render at all.
 *
 * Usage:
 *   const canEdit = useGate("invoice_edit");
 *   {canEdit && <Button label="Rozlicz" … />}
 */
export function useGate(gate: AccessGate): boolean {
  const { hasRole } = useCurrentEmployee();
  return hasRole(...ACCESS_GATES[gate]);
}

/**
 * Thin gate wrapper that prevents the inner component from mounting
 * when the user lacks the required role. No hooks from the inner
 * component run — zero data fetching, zero leaks.
 *
 * The inner component must accept `open: boolean` and `onClose: () => void`.
 *
 * Usage:
 *   const ProductEditModal = withGate("product_edit", ProductEditModalInner);
 */
export function withGate<P extends { open: boolean; onClose: () => void }>(
  gate: AccessGate,
  Inner: React.ComponentType<P>,
): React.FC<P> {
  const Guarded: React.FC<P> = (props) => {
    const { open, onClose } = props;
    const { hasRole } = useCurrentEmployee();
    const { message } = App.useApp();
    const { t } = useTranslation();
    const allowed = hasRole(...ACCESS_GATES[gate]);

    useEffect(() => {
      if (open && !allowed) {
        message.warning(t("common.errors.no_permission"));
        onClose();
      }
    }, [open, allowed, message, t, onClose]);

    if (!open || !allowed) return null;
    return React.createElement(Inner, props);
  };
  Guarded.displayName = `withGate(${gate}, ${Inner.displayName || Inner.name || "Component"})`;
  return Guarded;
}

/**
 * Gate wrapper for drawer/page content components.
 * Shows an Alert when the user lacks the required role.
 * Inner component never mounts — zero data fetching, zero leaks.
 *
 * Usage:
 *   const InvoiceSummaryContent = withContentGate("invoice_show", InvoiceSummaryContentInner);
 */
export function withContentGate<P extends object>(
  gate: AccessGate,
  Inner: React.ComponentType<P>,
): React.FC<P> {
  const Guarded: React.FC<P> = (props) => {
    const { hasRole } = useCurrentEmployee();
    const { t } = useTranslation();
    const allowed = hasRole(...ACCESS_GATES[gate]);

    if (!allowed) return React.createElement(Alert, { type: "warning", message: t("common.errors.no_permission") });
    return React.createElement(Inner, props);
  };
  Guarded.displayName = `withContentGate(${gate}, ${Inner.displayName || Inner.name || "Component"})`;
  return Guarded;
}
